import '../../styles/Episode.scss';

import React from 'react';
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { auth } from '../../firebase-config';
import { Link, useParams } from "react-router-dom";
import { useTvDetails } from "../../layouts/TvLayout";
import { useSeasonDetails } from "../media/Season";
import { EpisodeModel } from "../../models";
import { setBackgroundImage } from '../../helpers/helpers';

import InfoList from "../../components/InfoList";
import Loader from '../../components/Loader';
import PersonList from "../../components/PersonList";

const formatTime = (time: number | undefined) => {
    if (!time) return;
    const hours = Math.floor(time / 60);
    const minutes = time % 60;
    return `${hours > 0 ? `${hours}h ` : ''}${minutes}min`;
}

const formatSeasonEpisodeNo = (seasonNumber: number, episodeNumber: number) => {
    return `${seasonNumber}x${episodeNumber < 10 ? '0' + episodeNumber : episodeNumber}`;
}

const Episode = () => {
    const {season_number, episode_number} = useParams<{ season_number: string, episode_number: string }>();
    const tvDetails = useTvDetails();
    const seasonDetails = useSeasonDetails();

    const getEpisodeDetails = async () => {
        const apiKey = process.env.REACT_APP_TMDB_API_KEY;
        const response = await fetch(
            `https://api.themoviedb.org/3/tv/${tvDetails?.id}/season/${season_number}/episode/${episode_number}?append_to_response=credits`,
            {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${apiKey}`,
            }
        });
        return await response.json();
    }

    const { data: episodeDetails, isLoading, error} = useQuery<EpisodeModel>({
        queryKey: ['episode-details', tvDetails?.id, season_number, episode_number],
        queryFn: () => getEpisodeDetails(),
        enabled: !!tvDetails?.id && !!season_number && !!episode_number,
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return <Loader fullscreen message="Loading..." />;
    }
    if (error) {
        return <div>Error loading episode details: {error.message}</div>;
    }
    if (!episodeDetails) {
        return <div>No episode details found.</div>;
    }

    if (!tvDetails) {
        return <div>No TV show details found.</div>;
    }

    if (!seasonDetails) {
        return <div>No season details found.</div>;
    }

    episodeDetails.media_type = 'episode';
    setBackgroundImage(episodeDetails.still_path || tvDetails.backdrop_path);

    const runtime = episodeDetails.runtime || null;
    const infoList = [
        { label: 'Premiered', value: seasonDetails.air_date ? format(new Date(seasonDetails.air_date), 'd MMMM yyyy') : null },
        { label: 'Genres', value: tvDetails.genres?.map((genre: { name: string }) => genre.name).join(', ') },
        { label: 'Total Runtime', value: runtime && runtime > 0 ? formatTime(runtime) : null },
        { 
            label: 'Director',
            value: episodeDetails.credits?.crew?.filter(member => member.job === 'Director').map(director => ({
                id: director.id,
                name: director.name,
            })) || []
        },
        {
            label: 'Writer',
            value: episodeDetails.credits?.crew?.filter(member => ['Writer', 'Screenplay', 'Story'].includes(member.job)).map(writer => ({
                id: writer.id,
                name: writer.name,
            })) || []
        }
    ]

    document.title = `${tvDetails.name} ${season_number}x${episode_number} "${episodeDetails.name}"`;

    return (
        <>
            <div className="episode-summary-container">
                <div className="summary-poster">
                    <img src={`https://image.tmdb.org/t/p/w500${seasonDetails.poster_path || tvDetails.poster_path}`} alt={`Season ${season_number}`} />
                    {auth.currentUser && (
                        <>
                            <button className="history-button">Add to History</button>
                        </>
                    )}
                </div>

                <div className="episode-summary-content">
                    <div className="content-children">
                        <div className="title-container">
                            <span className="series-season-link">
                                <Link to={`/tv/${tvDetails.id}`}>{tvDetails.name} </Link>
                                 /
                                <Link to={`/tv/${tvDetails.id}/season/${season_number}`}> {seasonDetails.name} </Link>
                            </span>
                            <h1 className="episode-title">{formatSeasonEpisodeNo(seasonDetails.season_number, episodeDetails.episode_number)} {episodeDetails.name}</h1>

                            {seasonDetails.episodes.length > 1 && (
                                <div className="episode-picker">
                                    <div>Episode | </div>
                                    {seasonDetails.episodes.slice().reverse().map((episode: EpisodeModel) => (
                                        <div key={episode.id}>
                                            <Link to={`/tv/${tvDetails.id}/season/${season_number}/episode/${episode.episode_number}`} className={`episode-link ${episode_number && parseInt(episode_number) === episode.episode_number ? 'active' : ''}`}>
                                                {`${episode.episode_number}`}
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>


                    <p className="epsiode-overview">{episodeDetails.overview || seasonDetails.overview || tvDetails.overview}</p>
                </div>
            </div>

            <div className="episode-summary-container">
                <div className="episode-summary-content">
                    <InfoList items={infoList} />
                </div>
            </div>

            <div className="episode-person-container">
                <PersonList people={episodeDetails.credits?.cast} />
            </div>

        </>
    )
}

export default Episode;